'use client';

import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Building2, CheckCircle2, Clock, MapPin } from 'lucide-react';

import { cdnImageUrl } from '@kit/shared/storage';
import {
  EntityCard,
  EntityCardCaption,
  EntityCardCover,
  EntityCardKicker,
  EntityCardTitle,
} from '@kit/ui/entity-card';
import { Trans } from '@kit/ui/trans';

import { CastingCompensation } from '~/components/casting/casting-compensation';
import type { EligibilityResult } from '~/lib/auditions/audition-match';
import { formatTimestampDate, parseLocalDate } from '~/lib/date-utils';
import { useDisplayTimeZone } from '~/lib/hooks/use-display-time-zone';
import { useIsHydrated } from '~/lib/hooks/use-is-hydrated';

export interface AuditionWithApplication {
  id: string;
  slug: string | null;
  title: string | null;
  description: string | null;
  status: string;
  audition_type: string | null;
  opening_date: string | null;
  closing_date: string | null;
  apply_on_app: boolean | null;
  by_invitation_only: boolean | null;
  application_fee_amount: number | null;
  application_fee_currency: string | null;
  contract_start_date: string | null;
  contract_end_date: string | null;
  requirement_age_min: number | null;
  requirement_age_max: number | null;
  requirement_genders: string[] | null;
  requirement_dance_positions: string[] | null;
  featured: boolean | null;
  created_at: string;
  /** BAL-1205 casting pay; optional so non-listing callers need not select it. */
  compensation_amount?: number | null;
  compensation_currency?: string | null;
  compensation_unit?: string | null;
  /** Derived at the query site. For competitions, prefer
   * `competition_branding->>'cover_image_url'`. For non-competition auditions,
   * usually null until a producer uploads a cover. When null, the card
   * falls back to a hash-seeded gradient with initials per DESIGN.md
   * § Card archetype. */
  coverUrl?: string | null;
  /** The org's real logo (organizations.logo_media_id, resolved at the
   * query site). Byline fallback when accounts.picture_url is empty. */
  orgLogoUrl?: string | null;
  accounts: {
    id: string;
    name: string | null;
    slug: string | null;
    picture_url: string | null;
  } | null;
  audition_dates: {
    id: string;
    start_date: string;
    end_date: string | null;
    start_time: string | null;
    location: unknown | null;
  }[];
  userApplication: {
    id: string;
    status: string;
    createdAt: string;
  } | null;
  /** Per-dancer eligibility signal, attached server-side for signed-in dancers
   *  (page.tsx via evaluateEligibility). Absent for anon / non-dancer. */
  match?: EligibilityResult;
  /** True when surfaced by the "show closed" toggle — the role is closed, so the
   *  card renders greyed with a "Closed" badge and is not applyable (the detail
   *  page it links to shows the closed state). Absent/false for open roles. */
  isClosed?: boolean;
}

interface AuditionCardProps {
  audition: AuditionWithApplication;
  priority?: boolean;
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const APPLICATION_STATUS_LABELS: Record<
  string,
  { i18nKey: string; defaults: string }
> = {
  open: { i18nKey: 'auditions:status.pending', defaults: 'Pending' },
  submitted: { i18nKey: 'auditions:status.submitted', defaults: 'Submitted' },
  accepted: { i18nKey: 'auditions:status.accepted', defaults: 'Accepted' },
  rejected: { i18nKey: 'auditions:status.rejected', defaults: 'Rejected' },
  archived: { i18nKey: 'auditions:status.withdrawn', defaults: 'Withdrawn' },
};

function getInitials(title: string | null): string {
  if (!title) return 'AU';
  return title
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
}

export function AuditionCard({
  audition,
  priority = false,
}: AuditionCardProps) {
  const hasApplied = !!audition.userApplication;
  const applicationStatus = audition.userApplication?.status;

  // `closing_date` is an instant (the form takes a date AND a time), and
  // everything derived from it depends on the renderer: its time zone picks
  // the calendar day, its clock picks "days left". The server (UTC) and a
  // hydrating browser elsewhere disagree near midnight and React throws the
  // page away (BAL-1027). So the day is printed in UTC until hydration, then
  // in the visitor's zone, and the clock-derived facts render only once
  // hydration is done. The clock is read once at mount through lazy state
  // init: `Date.now()` is impure and the React Compiler's purity rule
  // rejects it in render or useMemo.
  const isHydrated = useIsHydrated();
  const timeZone = useDisplayTimeZone();
  const [mountedAt] = useState(() => Date.now());
  const closing = audition.closing_date;

  const isDeadlineApproaching = (() => {
    if (!isHydrated || !closing) return false;
    const closingTime = new Date(closing).getTime();
    return closingTime > mountedAt && closingTime < mountedAt + SEVEN_DAYS_MS;
  })();

  const daysToClose = (() => {
    if (!isHydrated || !closing) return null;
    const d = parseLocalDate(closing);
    if (!d) return null;
    const today = new Date(mountedAt);
    today.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - today.getTime()) / 86_400_000);
  })();

  // Compose the mono kicker. Lead with the next audition date, fall back
  // to the closing date if no scheduled session exists. When the deadline
  // is within seven days, add an inline "deadline" tail.
  const next = audition.audition_dates?.[0];
  const parts: string[] = [];
  if (next?.start_date) {
    // A `date` column: parsed as a local calendar day, the same everywhere.
    parts.push(format(parseLocalDate(next.start_date)!, 'MMM d, yyyy'));
  } else if (closing) {
    parts.push(
      `Deadline ${formatTimestampDate(closing, { year: 'numeric', month: 'short', day: 'numeric', timeZone })}`,
    );
  }
  if (isDeadlineApproaching && closing && next?.start_date) {
    parts.push(
      `Closes ${formatTimestampDate(closing, { month: 'short', day: 'numeric', timeZone })}`,
    );
  }
  const kickerLabel = parts.join(' · ');

  const initials = getInitials(audition.title);
  const orgName = audition.accounts?.name ?? null;
  const orgAvatar =
    audition.accounts?.picture_url ?? audition.orgLogoUrl ?? null;
  const detailHref = `/auditions/${audition.slug || audition.id}`;
  const fallbackSeed = audition.slug ?? audition.id;

  // At-a-glance facts (the research consensus card set: eligibility, location,
  // deadline urgency, fee). Location comes from the first dated session's jsonb.
  const location = useMemo(() => {
    for (const d of audition.audition_dates ?? []) {
      const loc = d.location as { city?: string; country?: string } | null;
      if (loc?.city || loc?.country) {
        return [loc.city, loc.country].filter(Boolean).join(', ');
      }
    }
    return null;
  }, [audition.audition_dates]);

  const isEligibleMatch =
    !!audition.match?.eligible && audition.match.totalCount > 0;
  const fee = audition.application_fee_amount ?? 0;

  return (
    <EntityCard
      href={detailHref}
      aspect="16/9"
      ariaLabel={audition.title || 'Audition'}
      fallbackSeed={fallbackSeed}
      testId="audition-card"
      className={audition.isClosed ? 'opacity-70 grayscale-[0.4]' : undefined}
    >
      <EntityCardCover
        src={audition.coverUrl ?? null}
        alt={audition.title || 'Audition cover'}
        fallbackInitials={initials}
        priority={priority}
        scrim
        pin={
          audition.featured && !orgName ? (
            <Trans i18nKey="auditions:featured" defaults="Featured" />
          ) : undefined
        }
        overlay={
          <>
            {orgName && (
              <span className="absolute top-2 left-2 z-20 inline-flex max-w-[70%] items-center gap-1.5 rounded-full bg-black/60 py-0.5 pr-2.5 pl-0.5 text-[11px] font-medium text-white shadow-sm backdrop-blur-sm">
                {orgAvatar ? (
                  /* eslint-disable-next-line @next/next/no-img-element -- rotating media URL, cdnImageUrl not next/image (image-rendering.md) */
                  <img
                    src={cdnImageUrl(orgAvatar, 'THUMBNAIL') ?? orgAvatar}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="size-5 shrink-0 rounded-full bg-white object-cover ring-1 ring-white/80"
                  />
                ) : (
                  <span
                    className="flex size-5 shrink-0 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/40"
                    aria-hidden
                  >
                    <Building2 className="size-3" aria-hidden />
                  </span>
                )}
                <span className="truncate">{orgName}</span>
              </span>
            )}
            {audition.isClosed && (
              <span className="absolute top-2 right-2 z-20 inline-flex items-center gap-1 rounded-md bg-black/70 px-2 py-0.5 text-[11px] font-medium text-white shadow-sm backdrop-blur-sm">
                <Trans i18nKey="auditions:status.closed" defaults="Closed" />
              </span>
            )}
            {!audition.isClosed && isEligibleMatch && (
              <span className="absolute top-2 right-2 z-20 inline-flex items-center gap-1 rounded-md bg-emerald-600/90 px-2 py-0.5 text-[11px] font-medium text-white shadow-sm backdrop-blur-sm">
                <CheckCircle2 className="size-3" aria-hidden />
                {audition.match!.totalCount > 1 ? (
                  <Trans
                    i18nKey="auditions:match.chipCount"
                    defaults="Matches {{n}} of {{m}}"
                    values={{
                      n: audition.match!.matchedCount,
                      m: audition.match!.totalCount,
                    }}
                  />
                ) : (
                  <Trans
                    i18nKey="auditions:match.chipEligible"
                    defaults="You match"
                  />
                )}
              </span>
            )}
            {(kickerLabel || location) && (
              <span className="absolute right-2.5 bottom-2 z-20 flex flex-col items-end gap-0.5 text-right text-white drop-shadow-sm">
                {kickerLabel && (
                  <span className="font-mono text-[10px] tracking-[0.16em] uppercase">
                    {kickerLabel}
                  </span>
                )}
                {location && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-white/85">
                    <MapPin className="size-3 shrink-0" aria-hidden />
                    <span className="max-w-[220px] truncate">{location}</span>
                  </span>
                )}
              </span>
            )}
          </>
        }
      />

      <EntityCardCaption>
        <EntityCardTitle>
          {audition.title || 'Untitled Audition'}
        </EntityCardTitle>

        {/* At-a-glance facts: location · fee, then deadline urgency. */}
        {(location || fee > 0 || audition.application_fee_amount === 0) && (
          <span className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
            {location && (
              <span className="inline-flex min-w-0 items-center gap-1">
                <MapPin className="size-3.5 shrink-0" aria-hidden />
                <span className="truncate">{location}</span>
              </span>
            )}
            {location && (fee > 0 || audition.application_fee_amount === 0) && (
              <span aria-hidden>·</span>
            )}
            {fee > 0 ? (
              <span>
                {audition.application_fee_currency ?? '€'}
                {fee}
              </span>
            ) : audition.application_fee_amount === 0 ? (
              <span className="text-emerald-600 dark:text-emerald-500">
                <Trans i18nKey="auditions:match.free" defaults="Free" />
              </span>
            ) : null}
          </span>
        )}

        <CastingCompensation
          audition={audition}
          className="text-foreground mt-1 text-[12px] font-medium"
        />

        {daysToClose !== null && daysToClose >= 0 && daysToClose <= 14 && (
          <span
            className={
              daysToClose <= 3
                ? 'mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-red-600 dark:text-red-500'
                : 'text-muted-foreground mt-1 inline-flex items-center gap-1 text-[11px] font-medium'
            }
          >
            <Clock className="size-3" aria-hidden />
            {daysToClose === 0 ? (
              <Trans
                i18nKey="auditions:match.closesToday"
                defaults="Closes today"
              />
            ) : (
              <Trans
                i18nKey="auditions:match.closesInDays"
                defaults="Closes in {{n}} days"
                values={{ n: daysToClose }}
              />
            )}
          </span>
        )}

        {(kickerLabel || (hasApplied && applicationStatus)) && (
          <EntityCardKicker>
            {kickerLabel}
            {hasApplied && applicationStatus && (
              <>
                {kickerLabel && ' · '}
                {APPLICATION_STATUS_LABELS[applicationStatus] ? (
                  <Trans
                    i18nKey={
                      APPLICATION_STATUS_LABELS[applicationStatus]!.i18nKey
                    }
                    defaults={
                      APPLICATION_STATUS_LABELS[applicationStatus]!.defaults
                    }
                  />
                ) : (
                  applicationStatus
                )}
              </>
            )}
          </EntityCardKicker>
        )}
      </EntityCardCaption>
    </EntityCard>
  );
}
